/**
 * HomeContentContext — fetches /api/home once and exposes the editable
 * section overrides so HomeLuxe can layer them on top of the hardcoded
 * defaults. The shape is `{ sections: { hero: {...}, brand_story: {...}, ... } }`.
 * Any missing key falls through to the built-in copy.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const Ctx = createContext(null);

export const HomeContentProvider = ({ children }) => {
  const [sections, setSections] = useState({});
  const [ready, setReady] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/home`);
      setSections(data?.sections || {});
    } catch {
      setSections({});
    } finally {
      setReady(true);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const value = useMemo(() => ({
    sections,
    ready,
    reload: load,
    // Return the merged copy for a given section: defaults + overrides.
    getSection: (key, defaults = {}) => ({ ...defaults, ...(sections?.[key] || {}) }),
  }), [sections, ready, load]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
};

export const useHomeContent = () => useContext(Ctx) || {
  sections: {},
  ready: true,
  reload: () => {},
  getSection: (_k, d = {}) => d,
};
